// frontend/src/app/components/projects/project-corpus/dialogs/attach-pdf-dialog.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { BaseAlertComponent } from '../../../alerts/base-alert/base-alert.component';
import { BaseModalComponent } from '../../../modals/base-modal/base-modal.component';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { ProjectPaper } from '../../../../project-corpus/domain/types';

const MAX_PDF_BYTES = 100 * 1024 * 1024;

/**
 * Attach-PDF dialog. Rendered (via `BaseModalComponent`) only when
 * `paper !== null`.
 *
 * Single-file drag-or-click picker accepting only PDFs. Enforces the 100 MB
 * size cap. Submit calls `service.attachPdf(paper.id, file)`. On success the
 * dialog closes; on error an inline `BaseAlertComponent` appears and the
 * dialog stays open.
 *
 * ESC and backdrop closing are handled by `BaseModalComponent`.
 */
@Component({
  selector: 'app-attach-pdf-dialog',
  standalone: true,
  imports: [CommonModule, BaseAlertComponent, BaseModalComponent],
  templateUrl: './attach-pdf-dialog.component.html',
  styleUrls: ['./attach-pdf-dialog.component.scss'],
})
export class AttachPdfDialogComponent {
  private readonly service = inject(ProjectCorpusService);

  private readonly _paper = signal<ProjectPaper | null>(null);
  readonly file = signal<File | null>(null);
  readonly fileError = signal<string | null>(null);
  readonly dragOver = signal(false);
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  @Input()
  set paper(value: ProjectPaper | null) {
    this._paper.set(value);
    if (value) {
      this.file.set(null);
      this.fileError.set(null);
      this.submitError.set(null);
    }
  }
  get paper(): ProjectPaper | null {
    return this._paper();
  }

  @Output() openChange = new EventEmitter<boolean>();

  readonly currentPaper = this._paper.asReadonly();
  readonly isOpen = computed(() => this._paper() !== null);
  readonly dialogTitle = computed(() => {
    const p = this._paper();
    return p?.hasPdf ? 'Change PDF' : 'Attach PDF';
  });

  close(): void {
    this._paper.set(null);
    this.openChange.emit(false);
  }

  onOpenChange(open: boolean): void {
    if (!open) {
      this.close();
    }
  }

  onPick(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    this.handleSelection(files);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    this.handleSelection(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
  }

  clearFile(): void {
    this.file.set(null);
    this.fileError.set(null);
  }

  submit(): void {
    if (this.submitting()) return;
    const paper = this._paper();
    const f = this.file();
    if (!paper || !f) return;

    this.submitting.set(true);
    this.submitError.set(null);
    this.service.attachPdf(paper.id, f).subscribe({
      next: () => {
        this.submitting.set(false);
        this.close();
      },
      error: (err: unknown) => {
        this.submitError.set(this.toMessage(err));
        this.submitting.set(false);
      },
    });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private handleSelection(files: File[]): void {
    this.fileError.set(null);
    if (files.length === 0) return;
    const f = files[0];
    if (f.size > MAX_PDF_BYTES) {
      this.fileError.set(`${f.name} (${this.formatSize(f.size)}) exceeds the 100 MB limit.`);
      return;
    }
    if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
      this.fileError.set(`${f.name} is not a PDF.`);
      return;
    }
    this.file.set(f);
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
